import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import axios from "axios";
import { useNotification } from "hooks/useNotification";
import userStore from "store/user.store";
import { User } from "types/user.type";
import instance from "./axiosInstance";
import { tokenStore } from "store/token.store";

export const ENDPOINT_LOGIN = "/auth/login";
export const ENDPOINT_LOGOUT = "/auth/logout";
export const ENDPOINT_REFRESH_TOKEN = "/auth/refresh";
export const ENDPOINT_SWITCH_USER = "/auth/switch-user";

export const useLogin = () => {
  const { setUser } = userStore();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { redirect?: string };

  const {
    mutate: login,
    isPending: isLoading,
    isSuccess,
    isError,
  } = useMutation({
    mutationFn: async (payload: LoginDTO): Promise<LoginResponse> =>
      (await instance.post(ENDPOINT_LOGIN, payload)).data,
    onSuccess: (data) => {
      setUser(data.user);
      tokenStore.setTokens({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        accessTokenExpiresIn: data.accessTokenExpiresIn,
        refreshTokenExpirationDate: new Date(data.refreshTokenExpirationDate)
          .getTime()
          .toString(),
      });

      tokenStore.updateTokenSetTime();

      navigate({ to: search.redirect || "/app" });
    },
  });

  return { login, isLoading, isSuccess, isError };
};

/**
 * Switches the active session to another user of the same company, authenticated
 * with that user's PIN code. Returns the same payload as login for the new user
 * and replaces the stored session. The whole query cache is cleared since all
 * cached data is scoped to the previous user.
 */
export const useSwitchUser = () => {
  const { setUser } = userStore();
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (payload: SwitchUserDTO): Promise<LoginResponse> =>
      (await instance.post(ENDPOINT_SWITCH_USER, payload)).data,
    onSuccess: (data) => {
      setUser(data.user);
      tokenStore.setTokens({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        accessTokenExpiresIn: data.accessTokenExpiresIn,
        refreshTokenExpirationDate: new Date(data.refreshTokenExpirationDate)
          .getTime()
          .toString(),
      });
      tokenStore.updateTokenSetTime();
      queryClient.clear();
    },
    onError: (error) => {
      // A rejected PIN is user-correctable, so give it an actionable toast;
      // network/server failures fall back to the module-wide generic message.
      const isRejectedPin =
        axios.isAxiosError(error) &&
        [400, 401, 403].includes(error.response?.status ?? 0);

      notification.error(
        isRejectedPin
          ? "Fel PIN-kod, försök igen"
          : "Något gick fel, kontakta support",
      );
    },
  });
};

export const useServerLogout = () => {
  const { clearUser } = userStore();

  return useMutation({
    mutationFn: async (): Promise<void> => await instance.post(ENDPOINT_LOGOUT),
    onSuccess: () => {
      clearUser();
      tokenStore.clearTokens();
    },
  });
};

export type LoginDTO = {
  username: string;
  password: string;
};

export type SwitchUserDTO = {
  userId: number;
  // String of exactly 4 chars — backend validates @IsString + @Length(4, 4).
  // Must stay a string so leading zeros survive.
  pinCode: string;
};

export type LoginResponse = {
  user: User;
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresIn: number;
  refreshTokenExpiresIn: number;
  refreshTokenExpirationDate: Date;
};
